import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Plus, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface Branch {
  id: string;
  name: string;
  isActive: boolean;
}

interface Product {
  id: string;
  name: string;
  sku: string;
}

interface StockTransferLineItem {
  id?: string;
  productId: string;
  quantity: number;
}

interface StockTransfer {
  id: string;
  transferNumber: string;
  fromBranchId: string;
  toBranchId: string;
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  notes?: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  receivedAt?: string;
  cancelledAt?: string;
  fromBranchName?: string;
  toBranchName?: string;
  items?: StockTransferLineItem[];
}

const StockTransfersPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({
    fromBranchId: '',
    toBranchId: '',
    notes: '',
  });
  const [items, setItems] = useState<StockTransferLineItem[]>([{ productId: '', quantity: 1 }]);

  const branchesQuery = useQuery<{ branches: Branch[] }>({
    queryKey: ['branches', 'activeOnly'],
    queryFn: async () => {
      const res = await api.get('/api/branches');
      return res.data;
    },
  });

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products', 'transfer_picker'],
    queryFn: async () => {
      const res = await api.get('/api/products');
      return res.data;
    },
  });

  const transfersQuery = useQuery<{ transfers: StockTransfer[] }>({
    queryKey: ['stockTransfers', statusFilter],
    queryFn: async () => {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const res = await api.get(`/api/stock-transfers${q}`);
      return res.data;
    },
  });

  const branches = (branchesQuery.data?.branches || []).filter((b) => b.isActive !== false);
  const products = productsQuery.data?.products || [];
  const transfers = transfersQuery.data?.transfers || [];

  const branchNameById = useMemo(() => {
    return new Map(branches.map((b) => [b.id, b.name]));
  }, [branches]);

  const filteredTransfers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return transfers;
    return transfers.filter((t) => {
      const fromName = (t.fromBranchName || branchNameById.get(t.fromBranchId) || '').toLowerCase();
      const toName = (t.toBranchName || branchNameById.get(t.toBranchId) || '').toLowerCase();
      return (
        (t.transferNumber || '').toLowerCase().includes(q) ||
        fromName.includes(q) ||
        toName.includes(q) ||
        (t.status || '').toLowerCase().includes(q)
      );
    });
  }, [transfers, searchTerm, branchNameById]);

  const resetForm = () => {
    setForm({ fromBranchId: '', toBranchId: '', notes: '' });
    setItems([{ productId: '', quantity: 1 }]);
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    resetForm();
  };

  const addItemRow = () => {
    setItems((prev) => [...prev, { productId: '', quantity: 1 }]);
  };

  const removeItemRow = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        fromBranchId: form.fromBranchId,
        toBranchId: form.toBranchId,
        notes: form.notes.trim() || undefined,
        items: items
          .filter((i) => i.productId && i.quantity > 0)
          .map((i) => ({ productId: i.productId, quantity: i.quantity })),
      };
      const res = await api.post('/api/stock-transfers', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
      toast.success('Transfer created');
      closeCreate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to create transfer');
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/stock-transfers/${id}/send`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Transfer sent');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to send transfer');
    },
  });

  const receiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/stock-transfers/${id}/receive`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Transfer received');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to receive transfer');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/stock-transfers/${id}/cancel`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stockTransfers'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Transfer cancelled');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to cancel transfer');
    },
  });

  const canCreate =
    form.fromBranchId &&
    form.toBranchId &&
    form.fromBranchId !== form.toBranchId &&
    items.some((i) => i.productId && i.quantity > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Transfers</h1>
          <p className="mt-1 text-sm text-gray-600">Move stock between branches/warehouses</p>
        </div>

        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Transfer
        </Button>
      </div>

      <div className="card">
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Search transfers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={<Search className="h-4 w-4 text-gray-400" />}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Transfers</h2>
          <div className="text-sm text-gray-600">Total: {filteredTransfers.length}</div>
        </div>

        <div className="card-body">
          {transfersQuery.isLoading ? (
            <div className="text-sm text-gray-600">Loading transfers...</div>
          ) : transfersQuery.isError ? (
            <div className="text-sm text-red-600">Failed to load transfers</div>
          ) : filteredTransfers.length === 0 ? (
            <div className="text-sm text-gray-600">No transfers found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransfers.map((t) => (
                    <tr key={t.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{t.transferNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{t.fromBranchName || branchNameById.get(t.fromBranchId) || t.fromBranchId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{t.toBranchName || branchNameById.get(t.toBranchId) || t.toBranchId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize">
                          {t.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{t.items?.length || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                        {t.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => sendMutation.mutate(t.id)}
                            loading={sendMutation.isPending}
                            disabled={sendMutation.isPending}
                          >
                            <ArrowLeftRight className="h-4 w-4 mr-2" />
                            Send
                          </Button>
                        )}
                        {t.status === 'sent' && (
                          <Button
                            size="sm"
                            onClick={() => receiveMutation.mutate(t.id)}
                            loading={receiveMutation.isPending}
                            disabled={receiveMutation.isPending}
                          >
                            Receive
                          </Button>
                        )}
                        {(t.status === 'draft' || t.status === 'sent') && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => cancelMutation.mutate(t.id)}
                            loading={cancelMutation.isPending}
                            disabled={cancelMutation.isPending}
                          >
                            Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">New Stock Transfer</h3>
              <button onClick={closeCreate} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Branch</label>
                  <select
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={form.fromBranchId}
                    onChange={(e) => setForm((p) => ({ ...p, fromBranchId: e.target.value }))}
                    disabled={branchesQuery.isLoading}
                  >
                    <option value="">Select...</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Branch</label>
                  <select
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                    value={form.toBranchId}
                    onChange={(e) => setForm((p) => ({ ...p, toBranchId: e.target.value }))}
                    disabled={branchesQuery.isLoading}
                  >
                    <option value="">Select...</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Input
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Optional"
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900">Items</h4>
                  <Button variant="secondary" size="sm" onClick={addItemRow}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                <div className="space-y-2">
                  {items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-8">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                        <select
                          className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                          value={it.productId}
                          onChange={(e) => {
                            const v = e.target.value;
                            setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, productId: v } : p)));
                          }}
                          disabled={productsQuery.isLoading}
                        >
                          <option value="">Select...</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name} ({p.sku})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-3">
                        <Input
                          label="Qty"
                          type="number"
                          value={String(it.quantity)}
                          onChange={(e) => {
                            const v = parseInt(e.target.value || '0', 10);
                            setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, quantity: v } : p)));
                          }}
                        />
                      </div>

                      <div className="md:col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItemRow(idx)}
                          disabled={items.length <= 1}
                          className="w-full"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button variant="secondary" onClick={closeCreate}>
                Cancel
              </Button>
              <Button
                onClick={() => createMutation.mutate()}
                disabled={!canCreate}
                loading={createMutation.isPending}
              >
                Create Transfer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransfersPage;
